import {
  type DunningHistoryEntrySchema,
  GetTenantSubscriptionResponseSchema,
  ListDunningHistoryResponseSchema,
  ListPlanFeaturesResponseSchema,
  ListSubscriptionPlansResponseSchema,
  ListTenantOverridesResponseSchema,
  TrialStatusSchema,
  type UpsertPlanFeatureInput,
  UpsertPlanFeatureResponseSchema,
  type UpsertTenantOverrideInput,
  UpsertTenantOverrideResponseSchema,
} from '@hrms/contracts/subscription';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { z as zod } from 'zod';

import { apiClient } from '@/lib/api-client';

export type SubscriptionPlan = z.infer<typeof ListSubscriptionPlansResponseSchema>['items'][number];
export type TenantSubscription = z.infer<
  typeof GetTenantSubscriptionResponseSchema
>['subscription'];

const PaymentConfigSchema = zod.object({
  keyId: zod.string().nullable(),
  configured: zod.boolean(),
});
export type PaymentConfig = z.infer<typeof PaymentConfigSchema>;

const CreateOrderResponseSchema = zod.object({
  orderId: zod.string().optional(),
  amount: zod.number().optional(),
  currency: zod.string().optional(),
  keyId: zod.string().nullable().optional(),
  error: zod.string().optional(),
});
export type CreateOrderResponse = z.infer<typeof CreateOrderResponseSchema>;

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscription', 'plans'],
    queryFn: async () => {
      return apiClient.get('/subscription/plans', ListSubscriptionPlansResponseSchema);
    },
  });
}

export function useCurrentSubscription() {
  return useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: async () => {
      return apiClient.get('/subscription', GetTenantSubscriptionResponseSchema);
    },
  });
}

export function usePaymentConfig() {
  return useQuery({
    queryKey: ['subscription', 'payment-config'],
    queryFn: async () => {
      return apiClient.get('/subscription/payment/config', PaymentConfigSchema);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export async function createPaymentOrder(
  planId: string,
  billingCycle: 'monthly' | 'annual',
): Promise<CreateOrderResponse> {
  return apiClient.post(
    '/subscription/payment/create-order',
    { planId, billingCycle },
    CreateOrderResponseSchema,
  );
}

const TrialStatusResponseSchema = zod.object({ data: TrialStatusSchema });
export type TrialStatus = z.infer<typeof TrialStatusSchema>;

export function useTrialStatus() {
  return useQuery({
    queryKey: ['trial-status'],
    queryFn: () =>
      apiClient.get('/subscription/trial/status', TrialStatusResponseSchema).then((r) => r.data),
  });
}

export function useStartTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      apiClient.post('/subscription/trial/start', { trialDays: days }, TrialStatusResponseSchema),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['trial-status'] }),
  });
}

// ---------------------------------------------------------------------------
// Dunning history (M22 — failed-payment audit log)
// ---------------------------------------------------------------------------

export type DunningHistoryEntry = z.infer<typeof DunningHistoryEntrySchema>;

export function useDunningHistory() {
  return useQuery({
    queryKey: ['dunning-history'],
    queryFn: async () => {
      const res = await apiClient.get(
        '/subscription/dunning-history',
        ListDunningHistoryResponseSchema,
      );
      return res.items;
    },
    staleTime: 60_000,
  });
}

const DunningRetryResponseSchema = zod.object({
  data: zod.object({
    attemptNumber: zod.number(),
    status: zod.enum(['failed', 'final_failure']),
  }),
});

export function useRetryDunning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.post('/subscription/dunning-history/retry', {}, DunningRetryResponseSchema),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dunning-history'] });
      void qc.invalidateQueries({ queryKey: ['subscription'] });
      void qc.invalidateQueries({ queryKey: ['trial-status'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Invoice PDF download (blob)
// ---------------------------------------------------------------------------

/**
 * Downloads an invoice PDF as a Blob and triggers a browser download.
 * Call directly — not a React Query hook because it's a one-shot imperative action.
 */
export async function downloadInvoicePdf(invoiceId: string, invoiceNumber: string): Promise<void> {
  const response = await fetch(`/api/subscription/invoices/${invoiceId}/pdf`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to download invoice PDF: ${response.statusText}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${invoiceNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Plan features (admin)
// ---------------------------------------------------------------------------

export type PlanFeatureItem = z.infer<typeof ListPlanFeaturesResponseSchema>['items'][number];
export type UpsertPlanFeaturePayload = UpsertPlanFeatureInput;

export function usePlanFeatures(planId: string | undefined) {
  return useQuery({
    queryKey: ['subscription', 'plan-features', planId],
    queryFn: async () => {
      if (!planId) return { items: [] };
      return apiClient.get(
        `/subscription/plans/${planId}/features`,
        ListPlanFeaturesResponseSchema,
      );
    },
    enabled: Boolean(planId),
  });
}

export function useUpsertPlanFeature(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertPlanFeaturePayload) =>
      apiClient.post(
        `/subscription/plans/${planId}/features`,
        input,
        UpsertPlanFeatureResponseSchema,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription', 'plan-features', planId] });
    },
  });
}

export function useDeletePlanFeature(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (featureKey: string) =>
      fetch(`/api/subscription/plans/${planId}/features/${encodeURIComponent(featureKey)}`, {
        method: 'DELETE',
        credentials: 'include',
      }).then((r) => {
        if (!r.ok) throw new Error('Failed to delete feature');
        return r.json() as Promise<{ featureKey: string; deletedAt: string }>;
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription', 'plan-features', planId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Tenant feature overrides (super-admin)
// ---------------------------------------------------------------------------

export type TenantOverrideItem = z.infer<typeof ListTenantOverridesResponseSchema>['items'][number];
export type UpsertTenantOverridePayload = UpsertTenantOverrideInput;

export function useTenantFeatureOverrides(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['subscription', 'tenant-overrides', tenantId],
    queryFn: async () => {
      if (!tenantId) return { items: [] };
      return apiClient.get(
        `/subscription/tenants/${tenantId}/feature-overrides`,
        ListTenantOverridesResponseSchema,
      );
    },
    enabled: Boolean(tenantId),
  });
}

export function useUpsertTenantOverride(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertTenantOverridePayload) =>
      apiClient.post(
        `/subscription/tenants/${tenantId}/feature-overrides`,
        input,
        UpsertTenantOverrideResponseSchema,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription', 'tenant-overrides', tenantId] });
    },
  });
}

export function useDeleteTenantOverride(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (featureKey: string) =>
      fetch(
        `/api/subscription/tenants/${tenantId}/feature-overrides/${encodeURIComponent(featureKey)}`,
        { method: 'DELETE', credentials: 'include' },
      ).then((r) => {
        if (!r.ok) throw new Error('Failed to delete override');
        return r.json() as Promise<{ featureKey: string; deletedAt: string }>;
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription', 'tenant-overrides', tenantId] });
    },
  });
}
